import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Stack,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Security as SecurityIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ViewComfy as ViewComfyIcon,
  GridView as GridViewIcon,
  ViewModule as ViewModuleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  WarningAmber as WarningIcon,
  VerifiedUser as VerifiedUserIcon,
  Science as ScienceIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';

interface NsfwStats {
  totalIncidents: number;
  last24h: number;
  last7d: number;
  uniqueDevices: number;
  uniqueUsers: number;
  csamTotal?: number;
  csamLast7d?: number;
}

interface OffenderSummary {
  deviceId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  userDisplayName?: string | null;
  attempts: number;
  lastAttempt: string;
  firstAttempt: string;
  lastMatchedClass: string;
  lastMatchedScore: number;
  lastActionTaken: string;
  deviceBlocked: boolean;
  userBlocked: boolean;
  deviceBypassed?: boolean;
  userBypassed?: boolean;
  lowestFaceAge?: number | null;
  highestFaceAge?: number | null;
  csamAttempts?: number;
}

interface NsfwIncident {
  _id: string;
  deviceId?: string;
  userId?: string;
  userEmail?: string;
  userDisplayName?: string;
  matchedClass: string;
  matchedScore: number;
  provider?: 'nudenet' | 'google_vision';
  attempts?: number;
  requestType: string;
  fileName?: string;
  imageStored: boolean;
  imagePath?: string;
  ipAddress?: string;
  platform?: string;
  createdAt: string;
  actionTaken?: string;
  minFaceAge?: number | null;
  maxFaceAge?: number | null;
  faceCount?: number;
  severity?: 'nsfw' | 'csam';
  isCsamSuspected?: boolean;
  // Server-enriched (see NsfwIncidentController._enrichIncidents)
  geo?: {
    ip?: string | null;
    country?: string | null;
    countryCode?: string | null;
    region?: string | null;
    city?: string | null;
    timezone?: string | null;
    isp?: string | null;
    org?: string | null;
    as?: string | null;
    hosting?: boolean;
    proxy?: boolean;
    ll?: number[] | null;
  } | null;
  // Enforced reviewer signals (RULE 0) — these force the NSFW check on even
  // when the global toggle is off and override any bypass.
  isGoogleTestDevice?: boolean;
  isDatacenterIp?: boolean;
  googleTestScore?: number;
  isEmulator?: boolean;
  nsfwForced?: boolean;
  nsfwForcedReasons?: string[];
  // Advisory-only build-fingerprint heuristic (never enforced).
  isGoogleTester?: boolean;
  testerReasons?: string[];
  deviceModel?: string | null;
  deviceBrand?: string | null;
  deviceRooted?: boolean;
  // Canonical device ID (incidents may be logged under a pre-registration ID).
  canonicalDeviceId?: string | null;
  // True when the incident's deviceId matches no device record at all.
  deviceUnknown?: boolean;
  // Forced always-on Vision scan (device- or account-level).
  forceVision?: boolean;
  deviceForceVision?: boolean;
  userForceVision?: boolean;
}

type GridSize = 'compact' | 'default' | 'comfortable';
type SeverityFilter = 'all' | 'nsfw' | 'csam';

const GRID_MIN_WIDTH: Record<GridSize, number> = {
  compact: 120,
  default: 170,
  comfortable: 250
};

// Quick auto-delete (retention) presets, in minutes. 0 = Off (keep forever).
const RETENTION_PRESETS: Array<{ m: number; label: string }> = [
  { m: 0, label: 'Off' },
  { m: 60, label: '1h' },
  { m: 1440, label: '24h' },
  { m: 10080, label: '7d' }
];

const formatRetentionLabel = (m: number): string => {
  if (m <= 0) return 'off';
  if (m >= 1440 && m % 1440 === 0) return `${m / 1440}d`;
  if (m >= 60 && m % 60 === 0) return `${m / 60}h`;
  return `${m} min`;
};

const formatAgeRange = (
  lo?: number | null,
  hi?: number | null
): { text: string; warn: boolean } => {
  if (lo == null && hi == null) return { text: '—', warn: false };
  if (lo == null) return { text: `${hi}`, warn: false };
  if (hi == null || lo === hi) return { text: `${lo}`, warn: lo < 18 };
  return { text: `${lo}–${hi}`, warn: lo < 18 };
};

const formatRelative = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

// Short "12m / 3h / 5d ago" style label for the thumbnail footer.
const formatAgeShort = (iso?: string) => {
  if (!iso) return '—';
  const mins = Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.floor(hrs)}h ago`;
  const days = hrs / 24;
  if (days < 30) return `${Math.floor(days)}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const isCsamIncident = (inc: NsfwIncident) =>
  !!inc.isCsamSuspected || inc.severity === 'csam';

// Human label for the detector that produced an incident.
const engineLabel = (provider?: string | null): string =>
  provider === 'google_vision' ? 'Vision' : 'NudeNet';

/**
 * Loads an admin-auth-protected incident image via authenticated axios and
 * returns an object URL. Fetches on mount, revokes on unmount. Blur is applied
 * by the caller via CSS so toggling it never triggers a re-fetch.
 */
const useIncidentImage = (incidentId: string | null, enabled: boolean) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!incidentId || !enabled) {
      setUrl(null);
      setError(null);
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    setLoading(true);
    setError(null);
    apiService
      .fetchNsfwIncidentImageBlob(incidentId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setUrl(u);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.status === 404
            ? 'Image not stored for this incident.'
            : err?.response?.data?.error || err?.message || 'Failed to load image.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [incidentId, enabled]);

  return { url, error, loading };
};

/** A single grid tile: blurred thumbnail (or placeholder) + badges + hover actions. */
const IncidentTile: React.FC<{
  incident: NsfwIncident;
  blur: boolean;
  blocked: boolean;
  bypassed: boolean;
  onView: () => void;
  onDelete: () => void;
}> = ({ incident, blur, blocked, bypassed, onView, onDelete }) => {
  const { url, loading } = useIncidentImage(incident._id, incident.imageStored);
  const csam = isCsamIncident(incident);
  const age = formatAgeRange(incident.minFaceAge, incident.maxFaceAge);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'grey.100',
        aspectRatio: '1 / 1',
        cursor: 'pointer',
        boxShadow: 1,
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        '&:hover': { transform: 'scale(1.015)', boxShadow: 4 },
        '&:hover .nsfw-overlay': { opacity: 1 }
      }}
      onClick={onView}
    >
      {/* Thumbnail / placeholder */}
      {incident.imageStored ? (
        url ? (
          <Box
            component="img"
            src={url}
            alt="blocked"
            loading="lazy"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'filter 0.3s',
              filter: blur ? 'blur(12px)' : 'none',
              transform: blur ? 'scale(1.1)' : 'none'
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress size={22} />
          </Box>
        )
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={0.5}
          sx={{ width: '100%', height: '100%', color: 'text.disabled', px: 1 }}
        >
          <ImageNotSupportedIcon />
          <Typography variant="caption" align="center">
            not stored
          </Typography>
        </Stack>
      )}

      {/* CSAM / age badges (top-left) */}
      <Box sx={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 0.5, zIndex: 2 }}>
        {csam && (
          <Chip label="CSAM" color="error" size="small" sx={{ height: 20, fontWeight: 700, fontSize: 10 }} />
        )}
        {!csam && age.warn && (
          <Chip
            icon={<WarningIcon sx={{ fontSize: 14 }} />}
            label={age.text}
            color="error"
            size="small"
            sx={{ height: 20, fontWeight: 700, fontSize: 10 }}
          />
        )}
        {/* Enforced reviewer signal (forces the NSFW check on) takes priority
            over the advisory fingerprint heuristic. */}
        {incident.nsfwForced ? (
          <Tooltip title={`NSFW forced on — ${(incident.nsfwForcedReasons || []).join(' · ')}`}>
            <Chip
              icon={<ScienceIcon sx={{ fontSize: 13 }} />}
              label="Reviewer"
              size="small"
              sx={{ height: 20, fontWeight: 700, fontSize: 10, bgcolor: '#f59e0b', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            />
          </Tooltip>
        ) : incident.isGoogleTester ? (
          <Tooltip title={`Advisory only — ${(incident.testerReasons || []).join(' · ')}`}>
            <Chip
              icon={<ScienceIcon sx={{ fontSize: 13 }} />}
              label="Tester?"
              size="small"
              sx={{ height: 20, fontWeight: 700, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: '#92400e' } }}
            />
          </Tooltip>
        ) : null}
      </Box>

      {/* Bypassed / Blocked badge (top-right) — bypass wins since it's the
          admin's explicit whitelist and overrides enforcement. */}
      {bypassed ? (
        <Chip
          icon={<VerifiedUserIcon sx={{ fontSize: 13 }} />}
          label="Bypassed"
          size="small"
          sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, height: 20, fontWeight: 700, fontSize: 10, bgcolor: '#f97316', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
        />
      ) : blocked ? (
        <Chip
          icon={<BlockIcon sx={{ fontSize: 13 }} />}
          label="Blocked"
          color="error"
          size="small"
          sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, height: 20, fontWeight: 600, fontSize: 10 }}
        />
      ) : null}

      {/* Hover overlay */}
      <Box
        className="nsfw-overlay"
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.55)',
          opacity: 0,
          transition: 'opacity 0.15s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          zIndex: 3
        }}
      >
        <Tooltip title="View details">
          <IconButton
            onClick={(e) => { e.stopPropagation(); onView(); }}
            sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.45)' } }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete this incident">
          <IconButton
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{ bgcolor: 'rgba(220,38,38,0.85)', color: '#fff', '&:hover': { bgcolor: 'rgb(220,38,38)' } }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Footer gradient: age + matched class */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          px: 1,
          py: 0.75,
          background: 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.35), transparent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          zIndex: 1
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 500 }} noWrap>
            {loading && incident.imageStored ? '…' : formatAgeShort(incident.createdAt)}
          </Typography>
          {/* Retry count — same image re-uploaded and re-blocked */}
          {(incident.attempts ?? 1) > 1 && (
            <Tooltip title={`Blocked ${incident.attempts} times — repeat uploads of this image bumped the count instead of adding rows`}>
              <Chip
                label={`×${incident.attempts}`}
                size="small"
                sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '& .MuiChip-label': { px: 0.6 } }}
              />
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: '62%' }}>
          {/* Detector / model badge */}
          <Chip
            label={engineLabel(incident.provider)}
            size="small"
            sx={{
              height: 16, fontSize: 9, fontWeight: 700, flexShrink: 0,
              bgcolor: incident.provider === 'google_vision' ? 'rgba(66,133,244,0.9)' : 'rgba(255,255,255,0.85)',
              color: incident.provider === 'google_vision' ? '#fff' : 'text.secondary',
              '& .MuiChip-label': { px: 0.6 }
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 10 }}
            noWrap
            title={`${incident.matchedClass} · ${Math.round((incident.matchedScore || 0) * 100)}%`}
          >
            {incident.matchedClass}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

/** Full-detail modal: large image + metadata + enforcement actions. */
const IncidentDetailModal: React.FC<{
  incident: NsfwIncident | null;
  blurDefault: boolean;
  blocked: boolean;
  bypassed: boolean;
  forceVision: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onBlock: () => void;
  onUnblock: () => void;
  onBypass: () => void;
  onUnbypass: () => void;
  onForceVision: () => void;
  onUnforceVision: () => void;
  onDelete: () => void;
  onDeleteOffender: () => void;
  busy: boolean;
}> = ({
  incident,
  blurDefault,
  blocked,
  bypassed,
  forceVision,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
  onBlock,
  onUnblock,
  onBypass,
  onUnbypass,
  onForceVision,
  onUnforceVision,
  onDelete,
  onDeleteOffender,
  busy
}) => {
  const navigate = useNavigate();
  const [blurred, setBlurred] = useState(blurDefault);
  React.useEffect(() => { setBlurred(blurDefault); }, [incident?._id, blurDefault]);

  const { url, error, loading } = useIncidentImage(
    incident?._id ?? null,
    !!incident?.imageStored
  );

  if (!incident) return null;
  const csam = isCsamIncident(incident);
  const age = formatAgeRange(incident.minFaceAge, incident.maxFaceAge);

  // "City, Region, Country" — skipping whichever parts the geo DB lacks.
  const geoLocation = [incident.geo?.city, incident.geo?.region, incident.geo?.country]
    .filter(Boolean)
    .join(', ');
  const deviceLabel = [incident.deviceBrand, incident.deviceModel].filter(Boolean).join(' ');

  const rows: Array<[string, React.ReactNode]> = [
    ['Detected category', <span style={{ fontFamily: 'monospace' }}>{incident.matchedClass} · {Math.round((incident.matchedScore || 0) * 100)}%</span>],
    ['Detector', incident.provider === 'google_vision' ? 'Google Vision (reviewer)' : 'NudeNet'],
    ...((incident.attempts ?? 1) > 1
      ? ([['Repeat blocks', `×${incident.attempts} — same image re-uploaded and re-blocked`]] as Array<[string, React.ReactNode]>)
      : []),
    ['Request type', incident.requestType],
    ['Face ages', (incident.faceCount ?? 0) === 0 ? 'no faces' : `${age.text}${(incident.faceCount ?? 0) > 1 ? ` · ${incident.faceCount} faces` : ''}`],
    ['Platform', incident.platform || '—'],
    ['Blocked at', formatRelative(incident.createdAt)],
    ['Account', incident.userEmail ? `${incident.userDisplayName ? incident.userDisplayName + ' · ' : ''}${incident.userEmail}` : '—'],
    ['Device', deviceLabel || '—'],
    [
      'Device ID',
      !incident.deviceId ? '—' : incident.deviceUnknown ? (
        // No device record matches this ID — don't offer a link that leads
        // nowhere. Usually means the app never completed registration.
        <Tooltip title="No device record matches this ID — the app most likely never completed registration">
          <Box component="span">
            <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              {incident.deviceId}
            </Typography>
            <Chip
              label="unregistered"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ ml: 1, height: 18, fontSize: 10 }}
            />
          </Box>
        </Tooltip>
      ) : (
        // Deep-link into the device page. Link on the canonical ID when the
        // incident was logged under a pre-registration alias.
        <Box
          component="span"
          onClick={() => {
            onClose();
            navigate(`/admin/devices?deviceId=${encodeURIComponent(incident.canonicalDeviceId || incident.deviceId!)}`);
          }}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            textDecoration: 'underline',
            fontFamily: 'monospace',
            fontSize: 12,
            '&:hover': { color: 'primary.dark' }
          }}
          title="Open this device in the Devices page"
        >
          {incident.deviceId}
        </Box>
      )
    ],
    ['IP address', incident.ipAddress || incident.geo?.ip || '—'],
    ['Location', geoLocation || '—'],
    ['Timezone', incident.geo?.timezone || '—'],
    ['Network', [incident.geo?.isp, incident.geo?.as].filter(Boolean).join(' · ') || '—']
  ];

  return (
    <Dialog open={!!incident} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Chip
          label={csam ? 'CSAM' : 'NSFW'}
          color={csam ? 'error' : 'warning'}
          size="small"
          variant={csam ? 'filled' : 'outlined'}
        />
        <Chip
          label={engineLabel(incident.provider)}
          size="small"
          sx={incident.provider === 'google_vision'
            ? { bgcolor: '#4285f4', color: '#fff' }
            : undefined}
          variant={incident.provider === 'google_vision' ? 'filled' : 'outlined'}
        />
        <span>Blocked image</span>
        {incident.isGoogleTester && (
          <Chip
            icon={<ScienceIcon />}
            label="Tester device"
            size="small"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: '#92400e' } }}
          />
        )}
        {bypassed && (
          <Chip
            icon={<VerifiedUserIcon />}
            label="NSFW bypassed"
            size="small"
            sx={{ bgcolor: '#f97316', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
          />
        )}
        {forceVision && (
          <Chip
            icon={<ScienceIcon />}
            label={incident.userForceVision && !incident.deviceForceVision ? 'Forced Vision (account)' : 'Forced Vision'}
            size="small"
            sx={{ bgcolor: '#4285f4', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
          />
        )}
        {blocked && <Chip icon={<BlockIcon />} label="Offender blocked" color="error" size="small" />}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {/* Image side */}
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'grey.900',
              minHeight: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {!incident.imageStored ? (
              <Stack alignItems="center" spacing={1} sx={{ color: 'grey.500', p: 4 }}>
                <ImageNotSupportedIcon fontSize="large" />
                <Typography variant="body2">Image not stored for this incident</Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
            ) : loading || !url ? (
              <CircularProgress sx={{ color: 'grey.400' }} />
            ) : (
              <>
                <Box
                  component="img"
                  src={url}
                  alt="blocked"
                  sx={{
                    width: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    transition: 'filter 0.2s',
                    filter: blurred ? 'blur(22px)' : 'none',
                    transform: blurred ? 'scale(1.05)' : 'none'
                  }}
                />
                <IconButton
                  onClick={() => setBlurred((b) => !b)}
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                >
                  {blurred ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                </IconButton>
                {blurred && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Typography
                      variant="caption"
                      sx={{ px: 1.5, py: 0.75, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff' }}
                    >
                      Explicit — click the eye to reveal
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Metadata side */}
          <Box sx={{ p: 2.5 }}>
            <Stack divider={<Divider flexItem />} spacing={1}>
              {rows.map(([label, value]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'baseline' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-all', fontFamily: label === 'Device ID' ? 'monospace' : undefined, fontSize: label === 'Device ID' ? 12 : undefined }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* RULE 0 — signals that force moderation on for this device */}
            {incident.nsfwForced && (
              <Alert severity="warning" icon={<ScienceIcon fontSize="small" />} sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  Reviewer / test device — NSFW check forced on
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                  Always scanned, even while the global check is off. A bypass does not apply.
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {(incident.nsfwForcedReasons || []).map((r, i) => (
                    <Typography component="li" variant="caption" key={i}>{r}</Typography>
                  ))}
                </Box>
              </Alert>
            )}

            {/* Advisory-only build-fingerprint heuristic — never enforced,
                since it also matches genuine retail handsets. */}
            {!incident.nsfwForced && incident.isGoogleTester && (incident.testerReasons || []).length > 0 && (
              <Alert severity="info" icon={<ScienceIcon fontSize="small" />} sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  Possible test build (advisory only — not enforced)
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {(incident.testerReasons || []).map((r, i) => (
                    <Typography component="li" variant="caption" key={i}>{r}</Typography>
                  ))}
                </Box>
              </Alert>
            )}

            {/* Enforcement actions */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Enforcement
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {blocked ? (
                <Button size="small" color="success" variant="outlined" startIcon={<UnblockIcon />} onClick={onUnblock} disabled={busy}>
                  Unblock offender
                </Button>
              ) : (
                <Button size="small" color="error" variant="contained" startIcon={<BlockIcon />} onClick={onBlock} disabled={busy}>
                  Block offender
                </Button>
              )}
              {bypassed ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VerifiedUserIcon />}
                  onClick={onUnbypass}
                  disabled={busy}
                  sx={{ color: '#c2410c', borderColor: '#f97316', '&:hover': { borderColor: '#c2410c', bgcolor: 'rgba(249,115,22,0.06)' } }}
                >
                  Remove NSFW bypass
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VerifiedUserIcon />}
                  onClick={onBypass}
                  disabled={busy}
                  sx={{ color: '#c2410c', borderColor: '#f97316', '&:hover': { borderColor: '#c2410c', bgcolor: 'rgba(249,115,22,0.06)' } }}
                >
                  Bypass NSFW for offender
                </Button>
              )}
              {forceVision ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ScienceIcon />}
                  onClick={onUnforceVision}
                  disabled={busy}
                  sx={{ color: '#1d4ed8', borderColor: '#4285f4', '&:hover': { borderColor: '#1d4ed8', bgcolor: 'rgba(66,133,244,0.06)' } }}
                >
                  Remove forced Vision
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ScienceIcon />}
                  onClick={onForceVision}
                  disabled={busy}
                  sx={{ color: '#1d4ed8', borderColor: '#4285f4', '&:hover': { borderColor: '#1d4ed8', bgcolor: 'rgba(66,133,244,0.06)' } }}
                >
                  Force Vision scan
                </Button>
              )}
              <Button size="small" color="inherit" variant="outlined" startIcon={<DeleteIcon />} onClick={onDelete} disabled={busy}>
                Delete incident
              </Button>
              <Button size="small" color="inherit" variant="outlined" startIcon={<DeleteIcon />} onClick={onDeleteOffender} disabled={busy}>
                Delete all for offender
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Blocking flags the device and account so future API requests are rejected.
              {' '}Bypass whitelists this device/account so its uploads skip the NSFW/CSAM check
              entirely — use only for trusted test devices.
              {' '}Force Vision always scans this device/account with Google Vision (overrides the
              global engine and any bypass); applied to the account, it follows the user onto any
              device.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          <Tooltip title="Previous">
            <span>
              <IconButton onClick={onPrev} disabled={!hasPrev}><ChevronLeftIcon /></IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next">
            <span>
              <IconButton onClick={onNext} disabled={!hasNext}><ChevronRightIcon /></IconButton>
            </span>
          </Tooltip>
        </Box>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const NsfwMonitoring: React.FC = () => {
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // View controls (persisted)
  const [gridSize, setGridSize] = useState<GridSize>(
    () => (localStorage.getItem('nsfw-grid-size') as GridSize) || 'default'
  );
  const [blurAll, setBlurAll] = useState<boolean>(() => {
    const saved = localStorage.getItem('nsfw-blur-all');
    return saved === null ? true : saved === 'true';
  });
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [engineFilter, setEngineFilter] = useState<'all' | 'nudenet' | 'google_vision'>('all');
  const [page, setPage] = useState(1);
  const limit = 30;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Confirmation dialogs
  const [blockDialog, setBlockDialog] = useState<{ open: boolean; incident: NsfwIncident | null; reason: string }>({
    open: false,
    incident: null,
    reason: 'NSFW policy violation'
  });
  const [deleteIncidentDialog, setDeleteIncidentDialog] = useState<{ open: boolean; incidentId: string | null }>({
    open: false,
    incidentId: null
  });
  const [deleteOffenderDialog, setDeleteOffenderDialog] = useState<{ open: boolean; incident: NsfwIncident | null }>({
    open: false,
    incident: null
  });
  // Bypass confirmation — lets the admin choose silent vs. notify, and edit the
  // notification title/body (pre-filled from the saved config, persisted on send).
  const [bypassDialog, setBypassDialog] = useState<{ open: boolean; incident: NsfwIncident | null }>({
    open: false,
    incident: null
  });
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyBody, setNotifyBody] = useState('');

  React.useEffect(() => { localStorage.setItem('nsfw-grid-size', gridSize); }, [gridSize]);
  React.useEffect(() => { localStorage.setItem('nsfw-blur-all', String(blurAll)); }, [blurAll]);

  const showSnack = (message: string, sev: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity: sev });

  // Queries
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['nsfwStats'],
    queryFn: async () => (await apiService.getNsfwStats()).data as NsfwStats
  });

  const { data: offenders } = useQuery({
    queryKey: ['nsfwOffenders'],
    queryFn: async () => ((await apiService.getNsfwOffenders(500)).data || []) as OffenderSummary[]
  });

  const { data: incidentsResp, isLoading: incidentsLoading, refetch: refetchIncidents } = useQuery({
    queryKey: ['nsfwIncidentsGrid', page],
    queryFn: async () => {
      const r = await apiService.getNsfwIncidents({ page, limit });
      return r.data as { items: NsfwIncident[]; total: number };
    }
  });

  const { data: appCfg } = useQuery({
    queryKey: ['appConfig'],
    queryFn: async () =>
      (await apiService.getAppConfig()).data as {
        nsfwStoreImages?: boolean;
        nsfwIncidentRetentionMinutes?: number;
        nsfwBypassNotifyTitle?: string;
        nsfwBypassNotifyBody?: string;
      }
  });

  const storeImages = !!appCfg?.nsfwStoreImages;

  // Auto-delete (retention) — seeded from config, editable, saved on demand.
  const [retentionMinutes, setRetentionMinutes] = useState(0);
  const [retentionMsg, setRetentionMsg] = useState<string | null>(null);
  React.useEffect(() => {
    if (typeof appCfg?.nsfwIncidentRetentionMinutes === 'number') {
      setRetentionMinutes(appCfg.nsfwIncidentRetentionMinutes);
    }
  }, [appCfg?.nsfwIncidentRetentionMinutes]);

  const retentionMutation = useMutation({
    mutationFn: async (minutes: number) =>
      apiService.updateNsfwSettings({ nsfwIncidentRetentionMinutes: minutes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setRetentionMsg('Saved ✓');
      showSnack('Auto-delete setting saved');
    },
    onError: (e: any) => {
      setRetentionMsg('Failed to save');
      showSnack(e?.response?.data?.error || 'Failed to save auto-delete setting', 'error');
    }
  });

  // Block- and bypass-status lookups, built from the offender summary.
  const { blockedDevices, blockedUsers, bypassedDevices, bypassedUsers } = useMemo(() => {
    const bd = new Set<string>();
    const bu = new Set<string>();
    const pd = new Set<string>();
    const pu = new Set<string>();
    (offenders || []).forEach((o) => {
      if (o.deviceBlocked && o.deviceId) bd.add(o.deviceId);
      if (o.userBlocked && o.userId) bu.add(String(o.userId));
      if (o.deviceBypassed && o.deviceId) pd.add(o.deviceId);
      if (o.userBypassed && o.userId) pu.add(String(o.userId));
    });
    return { blockedDevices: bd, blockedUsers: bu, bypassedDevices: pd, bypassedUsers: pu };
  }, [offenders]);

  const isBlocked = (inc: NsfwIncident) =>
    (!!inc.deviceId && blockedDevices.has(inc.deviceId)) ||
    (!!inc.userId && blockedUsers.has(String(inc.userId)));

  const isBypassed = (inc: NsfwIncident) =>
    (!!inc.deviceId && bypassedDevices.has(inc.deviceId)) ||
    (!!inc.userId && bypassedUsers.has(String(inc.userId)));

  const allItems = useMemo(() => incidentsResp?.items || [], [incidentsResp]);
  const items = useMemo(() => {
    return allItems.filter((i) => {
      const sevOk = severity === 'all' ? true : (severity === 'csam' ? isCsamIncident(i) : !isCsamIncident(i));
      const engOk = engineFilter === 'all' ? true : (i.provider || 'nudenet') === engineFilter;
      return sevOk && engOk;
    });
  }, [allItems, severity, engineFilter]);

  const total = incidentsResp?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // Mutations
  const blockMutation = useMutation({
    mutationFn: async (payload: { deviceId?: string; userId?: string; reason: string }) =>
      apiService.blockNsfwOffender(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      setBlockDialog({ open: false, incident: null, reason: 'NSFW policy violation' });
      showSnack('Offender blocked successfully');
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to block offender', 'error')
  });

  const unblockMutation = useMutation({
    mutationFn: async (payload: { deviceId?: string; userId?: string }) =>
      apiService.unblockNsfwOffender(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      showSnack('Offender unblocked successfully');
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to unblock offender', 'error')
  });

  const bypassMutation = useMutation({
    mutationFn: async (payload: {
      deviceId?: string;
      userId?: string;
      notify?: boolean;
      notifyTitle?: string;
      notifyBody?: string;
    }) => apiService.bypassNsfwOffender({ ...payload, reason: 'Admin NSFW bypass' }),
    onSuccess: (resp: any) => {
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setBypassDialog({ open: false, incident: null });
      const n = resp?.data?.notification;
      if (n) {
        showSnack(
          n.notified
            ? `Bypassed — notification sent${n.sentCount > 1 ? ` to ${n.sentCount} devices` : ''}`
            : `Bypassed, but notification not sent: ${n.error || 'no push token on file'}`,
          n.notified ? 'success' : 'error'
        );
      } else {
        showSnack('NSFW check bypassed for this device/account');
      }
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to grant NSFW bypass', 'error')
  });

  const unbypassMutation = useMutation({
    mutationFn: async (payload: { deviceId?: string; userId?: string }) =>
      apiService.unbypassNsfwOffender(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      showSnack('NSFW bypass removed');
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to remove NSFW bypass', 'error')
  });

  const forceVisionMutation = useMutation({
    mutationFn: async (payload: { deviceId?: string; userId?: string; userEmail?: string }) =>
      apiService.forceVisionNsfwOffender({ ...payload, reason: 'Admin forced Vision scan' }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['nsfwIncidentsGrid'] });
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      showSnack(vars.userId || vars.userEmail
        ? 'Forced Vision scan enabled for this account (applies on any device)'
        : 'Forced Vision scan enabled for this device');
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to enable forced Vision', 'error')
  });

  const unforceVisionMutation = useMutation({
    mutationFn: async (payload: { deviceId?: string; userId?: string; userEmail?: string }) =>
      apiService.unforceVisionNsfwOffender(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nsfwIncidentsGrid'] });
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      showSnack('Forced Vision scan removed');
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to remove forced Vision', 'error')
  });

  const deleteIncidentMutation = useMutation({
    mutationFn: async (incidentId: string) => apiService.deleteNsfwIncident(incidentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nsfwIncidentsGrid'] });
      queryClient.invalidateQueries({ queryKey: ['nsfwStats'] });
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      setDeleteIncidentDialog({ open: false, incidentId: null });
      setSelectedIndex(null);
      showSnack('Incident deleted');
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to delete incident', 'error')
  });

  const deleteOffenderMutation = useMutation({
    mutationFn: async (payload: { deviceId?: string; userId?: string }) =>
      apiService.deleteNsfwOffender(payload),
    onSuccess: (resp: any) => {
      queryClient.invalidateQueries({ queryKey: ['nsfwIncidentsGrid'] });
      queryClient.invalidateQueries({ queryKey: ['nsfwStats'] });
      queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
      setDeleteOffenderDialog({ open: false, incident: null });
      setSelectedIndex(null);
      const count = resp?.data?.deletedCount ?? 0;
      showSnack(`Deleted ${count} incident${count === 1 ? '' : 's'} for offender`);
    },
    onError: (e: any) => showSnack(e?.response?.data?.error || 'Failed to delete offender history', 'error')
  });

  const busy =
    blockMutation.isPending ||
    unblockMutation.isPending ||
    bypassMutation.isPending ||
    unbypassMutation.isPending ||
    forceVisionMutation.isPending ||
    unforceVisionMutation.isPending ||
    deleteIncidentMutation.isPending ||
    deleteOffenderMutation.isPending;

  const handleRefresh = () => {
    refetchStats();
    refetchIncidents();
    queryClient.invalidateQueries({ queryKey: ['nsfwOffenders'] });
  };

  const handleBlockConfirm = () => {
    const o = blockDialog.incident;
    if (!o) return;
    blockMutation.mutate({
      deviceId: o.deviceId || undefined,
      userId: o.userId || undefined,
      reason: blockDialog.reason
    });
  };

  const selected = selectedIndex != null ? items[selectedIndex] ?? null : null;

  const statCards = [
    { label: 'Total', value: stats?.totalIncidents },
    { label: 'Last 24h', value: stats?.last24h },
    { label: 'Last 7d', value: stats?.last7d },
    { label: 'Devices', value: stats?.uniqueDevices },
    { label: 'Users', value: stats?.uniqueUsers },
    { label: 'CSAM total', value: stats?.csamTotal, csam: true },
    { label: 'CSAM 7d', value: stats?.csamLast7d, csam: true }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            <Typography variant="h5">Blocked Images</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Images rejected by NSFW moderation
            {total > 0 && <> · <strong>{total.toLocaleString()}</strong> total</>}
          </Typography>
        </Box>
      </Box>

      {/* Stat chips */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {statCards.map((c) => (
          <Chip
            key={c.label}
            label={
              <span>
                {c.label}: <strong>{c.value ?? 0}</strong>
              </span>
            }
            size="small"
            color={c.csam && (c.value ?? 0) > 0 ? 'error' : 'default'}
            variant={c.csam ? 'outlined' : 'filled'}
            sx={c.csam ? { borderColor: 'error.main' } : undefined}
          />
        ))}
      </Stack>

      {/* Controls bar */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* Severity filter (applies to the current page) */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={severity}
              onChange={(_, v) => v && setSeverity(v)}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="nsfw">NSFW</ToggleButton>
              <ToggleButton value="csam" sx={{ color: 'error.main' }}>CSAM</ToggleButton>
            </ToggleButtonGroup>

            {/* Detector / engine filter (current page) */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={engineFilter}
              onChange={(_, v) => v && setEngineFilter(v)}
            >
              <ToggleButton value="all">All engines</ToggleButton>
              <ToggleButton value="nudenet">NudeNet</ToggleButton>
              <ToggleButton value="google_vision">Vision</ToggleButton>
            </ToggleButtonGroup>

            <Box flex={1} />

            {/* Grid size */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={gridSize}
              onChange={(_, v) => v && setGridSize(v)}
            >
              <ToggleButton value="compact" title="Compact"><ViewComfyIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="default" title="Default"><GridViewIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="comfortable" title="Comfortable"><ViewModuleIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>

            {/* Blur toggle */}
            <Tooltip title={blurAll ? 'Reveal images' : 'Blur images'}>
              <IconButton
                onClick={() => setBlurAll((v) => !v)}
                sx={{
                  bgcolor: blurAll ? 'warning.light' : 'transparent',
                  color: blurAll ? 'warning.dark' : 'text.secondary'
                }}
              >
                {blurAll ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh}>
                <RefreshIcon sx={incidentsLoading ? { animation: 'spin 1s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } } : undefined} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Auto-delete (retention) row */}
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mr: 0.5 }}>
              Auto-delete after:
            </Typography>
            {RETENTION_PRESETS.map((p) => (
              <Button
                key={p.m}
                size="small"
                variant={retentionMinutes === p.m ? 'contained' : 'outlined'}
                color={p.m === 0 ? 'inherit' : 'primary'}
                onClick={() => { setRetentionMinutes(p.m); setRetentionMsg(null); }}
                sx={{ minWidth: 44, px: 1 }}
              >
                {p.label}
              </Button>
            ))}
            <TextField
              size="small"
              type="number"
              label="minutes"
              value={retentionMinutes}
              onChange={(e) => { setRetentionMinutes(Math.max(0, parseInt(e.target.value, 10) || 0)); setRetentionMsg(null); }}
              inputProps={{ min: 0 }}
              sx={{ width: 110 }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => retentionMutation.mutate(retentionMinutes)}
              disabled={retentionMutation.isPending || retentionMinutes === (appCfg?.nsfwIncidentRetentionMinutes ?? 0)}
            >
              Save
            </Button>
            {retentionMsg && (
              <Typography variant="caption" color="text.secondary">{retentionMsg}</Typography>
            )}
            <Box sx={{ flexBasis: '100%' }} />
            <Typography variant="caption" color="text.secondary">
              {retentionMinutes > 0 ? (
                <>Incidents older than <strong>{formatRetentionLabel(retentionMinutes)}</strong> are deleted automatically (swept every ~5&nbsp;min, images included). CSAM-suspected incidents are never auto-deleted.</>
              ) : (
                <>Off — incidents are kept until you delete them manually.</>
              )}
            </Typography>
          </Box>

          {!storeImages && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Image storage is currently <strong>disabled</strong> — tiles show detection metadata
              only. Enable <em>“Store NSFW images for review”</em> in App Configuration to retain
              the offending image for future incidents.
            </Alert>
          )}
          {(severity !== 'all' || engineFilter !== 'all') && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Filters apply to the current page ({items.length} of {allItems.length} shown).
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Grid */}
      {incidentsLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_MIN_WIDTH[gridSize]}px, 1fr))`,
            gap: 1.5
          }}
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <Box key={i} sx={{ aspectRatio: '1 / 1', borderRadius: 2, bgcolor: 'grey.100', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '50%': { opacity: 0.5 } } }} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Box textAlign="center" py={6}>
              <Typography sx={{ fontSize: 40, mb: 1 }}>🛡️</Typography>
              <Typography variant="subtitle1" fontWeight="medium">
                {allItems.length === 0 ? 'No blocked images' : `No ${severity.toUpperCase()} incidents on this page`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Images rejected by NSFW moderation will appear here.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_MIN_WIDTH[gridSize]}px, 1fr))`,
            gap: 1.5
          }}
        >
          {items.map((inc, index) => (
            <IncidentTile
              key={inc._id}
              incident={inc}
              blur={blurAll}
              blocked={isBlocked(inc)}
              bypassed={isBypassed(inc)}
              onView={() => setSelectedIndex(index)}
              onDelete={() => setDeleteIncidentDialog({ open: true, incidentId: inc._id })}
            />
          ))}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && severity === 'all' && engineFilter === 'all' && (
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={3}>
          <Button variant="outlined" size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <Typography variant="body2" color="text.secondary">Page {page} of {totalPages}</Typography>
          <Button variant="outlined" size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </Box>
      )}

      {/* Detail modal */}
      <IncidentDetailModal
        incident={selected}
        blurDefault={blurAll}
        blocked={selected ? isBlocked(selected) : false}
        bypassed={selected ? isBypassed(selected) : false}
        forceVision={selected?.forceVision === true}
        hasPrev={selectedIndex != null && selectedIndex > 0}
        hasNext={selectedIndex != null && selectedIndex < items.length - 1}
        busy={busy}
        onPrev={() => setSelectedIndex((i) => (i != null && i > 0 ? i - 1 : i))}
        onNext={() => setSelectedIndex((i) => (i != null && i < items.length - 1 ? i + 1 : i))}
        onClose={() => setSelectedIndex(null)}
        onBlock={() => selected && setBlockDialog({ open: true, incident: selected, reason: 'NSFW policy violation' })}
        onUnblock={() => selected && unblockMutation.mutate({ deviceId: selected.deviceId || undefined, userId: selected.userId || undefined })}
        onBypass={() => {
          if (!selected) return;
          setNotifyTitle(appCfg?.nsfwBypassNotifyTitle ?? 'Premium Feature Enabled');
          setNotifyBody(appCfg?.nsfwBypassNotifyBody ?? 'Safety features disabled. Enjoy unrestricted uploads on your device.');
          setBypassDialog({ open: true, incident: selected });
        }}
        onUnbypass={() => selected && unbypassMutation.mutate({ deviceId: selected.deviceId || undefined, userId: selected.userId || undefined })}
        onForceVision={() => selected && forceVisionMutation.mutate({ deviceId: (selected.canonicalDeviceId || selected.deviceId) || undefined, userId: selected.userId || undefined, userEmail: selected.userEmail || undefined })}
        onUnforceVision={() => selected && unforceVisionMutation.mutate({ deviceId: (selected.canonicalDeviceId || selected.deviceId) || undefined, userId: selected.userId || undefined, userEmail: selected.userEmail || undefined })}
        onDelete={() => selected && setDeleteIncidentDialog({ open: true, incidentId: selected._id })}
        onDeleteOffender={() => selected && setDeleteOffenderDialog({ open: true, incident: selected })}
      />

      {/* Block confirmation */}
      <Dialog
        open={blockDialog.open}
        onClose={() => setBlockDialog({ open: false, incident: null, reason: 'NSFW policy violation' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Block offender</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This permanently blocks the offender from face swap features. The device and the
            signed-in account (when known) are both flagged; subsequent API requests are rejected
            by the authentication middleware.
          </Typography>
          {blockDialog.incident?.userEmail && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Account</Typography>
              <Typography variant="body2">{blockDialog.incident.userEmail}</Typography>
            </Box>
          )}
          {blockDialog.incident?.deviceId && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Device ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {blockDialog.incident.deviceId}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <TextField
            label="Block reason"
            fullWidth
            value={blockDialog.reason}
            onChange={(e) => setBlockDialog((prev) => ({ ...prev, reason: e.target.value }))}
            helperText="Stored on the device/user record; surfaced in 403 responses for support investigations."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialog({ open: false, incident: null, reason: 'NSFW policy violation' })}>
            Cancel
          </Button>
          <Button color="error" variant="contained" startIcon={<BlockIcon />} onClick={handleBlockConfirm} disabled={blockMutation.isPending}>
            Confirm block
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete single incident */}
      <Dialog
        open={deleteIncidentDialog.open}
        onClose={() => setDeleteIncidentDialog({ open: false, incidentId: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this incident?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            The incident record is removed from the moderation queue. Any stored image on disk is
            deleted too.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This cannot be undone. The offender's other incidents and block status are NOT affected.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIncidentDialog({ open: false, incidentId: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={deleteIncidentMutation.isPending}
            onClick={() => deleteIncidentDialog.incidentId && deleteIncidentMutation.mutate(deleteIncidentDialog.incidentId)}
          >
            Delete incident
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete all for offender */}
      <Dialog
        open={deleteOffenderDialog.open}
        onClose={() => setDeleteOffenderDialog({ open: false, incident: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete all incidents for this offender?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            All NSFW incident records for this offender are removed from the moderation queue, along
            with any stored images.
          </Typography>
          {deleteOffenderDialog.incident?.userEmail && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Account</Typography>
              <Typography variant="body2">{deleteOffenderDialog.incident.userEmail}</Typography>
            </Box>
          )}
          {deleteOffenderDialog.incident?.deviceId && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Device ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {deleteOffenderDialog.incident.deviceId}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This cannot be undone. The offender's <strong>block status is NOT cleared</strong> — use
            "Unblock" separately if you want to lift the block.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOffenderDialog({ open: false, incident: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={deleteOffenderMutation.isPending}
            onClick={() => deleteOffenderDialog.incident && deleteOffenderMutation.mutate({
              deviceId: deleteOffenderDialog.incident.deviceId || undefined,
              userId: deleteOffenderDialog.incident.userId || undefined
            })}
          >
            Delete all incidents
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bypass confirmation — silent vs. notify, with editable notification copy */}
      <Dialog
        open={bypassDialog.open}
        onClose={() => !bypassMutation.isPending && setBypassDialog({ open: false, incident: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bypass NSFW check</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Allow{' '}
            <strong>
              {bypassDialog.incident?.userEmail ||
                (bypassDialog.incident?.deviceId
                  ? `device ${bypassDialog.incident.deviceId.slice(0, 16)}…`
                  : 'this offender')}
            </strong>{' '}
            to skip the NSFW/CSAM check on future uploads. Optionally notify them with a push
            notification.
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            A bypass disables CSAM screening too — use only for trusted test devices.
          </Alert>

          {bypassDialog.incident?.nsfwForced && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a detected <strong>reviewer / test device</strong>, so the NSFW check is
              forced on and this bypass <strong>will not apply</strong> to it.
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Notification (only sent if you choose “Notify &amp; bypass”)
          </Typography>
          <TextField
            label="Title"
            fullWidth
            size="small"
            value={notifyTitle}
            onChange={(e) => setNotifyTitle(e.target.value)}
            sx={{ mt: 1.5 }}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Body"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={notifyBody}
            onChange={(e) => setNotifyBody(e.target.value)}
            sx={{ mt: 1.5 }}
            inputProps={{ maxLength: 500 }}
            helperText="Saved as the default for next time when you notify."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBypassDialog({ open: false, incident: null })}
            disabled={bypassMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={bypassMutation.isPending}
            onClick={() => {
              const o = bypassDialog.incident;
              if (!o) return;
              bypassMutation.mutate({ deviceId: o.deviceId || undefined, userId: o.userId || undefined, notify: false });
            }}
          >
            Bypass silently
          </Button>
          <Button
            variant="contained"
            disabled={bypassMutation.isPending}
            sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#c2410c' } }}
            onClick={() => {
              const o = bypassDialog.incident;
              if (!o) return;
              bypassMutation.mutate({
                deviceId: o.deviceId || undefined,
                userId: o.userId || undefined,
                notify: true,
                notifyTitle,
                notifyBody
              });
            }}
          >
            Notify &amp; bypass
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NsfwMonitoring;
